type CapacityValue = number | string | null | undefined

export type VolunteeringWeekCapacitySource = {
    availableSpots?: CapacityValue
    maxVolunteerCount?: CapacityValue
    receivedVolunteerRequestsCount?: CapacityValue
}

export type VolunteeringWeekCampaignCapacityProjectSource = VolunteeringWeekCapacitySource & {
    startDateAt?: unknown
    when?: unknown
}

export type VolunteeringWeekCampaignCapacityRequestSource = {
    createdAt?: unknown
    status?: string | null
    volunteerCount?: unknown
}

const VOLUNTEERING_WEEK_EXCLUDED_CAPACITY_REQUEST_STATUSES = new Set(['cancelled', 'rejected'])

export function normalizeVolunteeringWeekCapacityCount(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.floor(value))
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : null
    }

    return null
}

export function parseVolunteeringWeekCapacityDate(value: unknown): Date | null {
    if (!value) {
        return null
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }

    if (typeof (value as { toDate?: unknown }).toDate === 'function') {
        const date = (value as { toDate: () => Date }).toDate()
        return Number.isNaN(date.getTime()) ? null : date
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }

    if (typeof (value as { _seconds?: unknown })._seconds === 'number') {
        return new Date((value as { _seconds: number })._seconds * 1000)
    }

    if (typeof (value as { seconds?: unknown }).seconds === 'number') {
        return new Date((value as { seconds: number }).seconds * 1000)
    }

    return null
}

export function getVolunteeringWeekCapacityCampaignYear(
    project: VolunteeringWeekCampaignCapacityProjectSource
): number | null {
    const campaignDate =
        parseVolunteeringWeekCapacityDate(project.startDateAt) ?? parseVolunteeringWeekCapacityDate(project.when)
    return campaignDate?.getFullYear() ?? null
}

export function getVolunteeringWeekCampaignReceivedVolunteerRequestsCount(
    project: VolunteeringWeekCampaignCapacityProjectSource,
    requests: VolunteeringWeekCampaignCapacityRequestSource[]
): number | null {
    const campaignYear = getVolunteeringWeekCapacityCampaignYear(project)

    if (campaignYear === null) {
        return null
    }

    return requests.reduce((total, request) => {
        const requestDate = parseVolunteeringWeekCapacityDate(request.createdAt)

        if (!requestDate || requestDate.getFullYear() !== campaignYear) {
            return total
        }

        if (request.status && VOLUNTEERING_WEEK_EXCLUDED_CAPACITY_REQUEST_STATUSES.has(request.status.toLowerCase())) {
            return total
        }

        return total + normalizeVolunteeringWeekRequestedVolunteerCount(request.volunteerCount)
    }, 0)
}

export function normalizeVolunteeringWeekRequestedVolunteerCount(value: unknown, fallback = 1): number {
    return normalizeVolunteeringWeekCapacityCount(value) ?? fallback
}

export function getAvailableVolunteeringWeekSpots(project: VolunteeringWeekCapacitySource): number | null {
    const explicitAvailableSpots = normalizeVolunteeringWeekCapacityCount(project.availableSpots)
    if (explicitAvailableSpots !== null) {
        return explicitAvailableSpots
    }

    const maxVolunteerCount = normalizeVolunteeringWeekCapacityCount(project.maxVolunteerCount)
    if (maxVolunteerCount === null) {
        return null
    }

    const receivedVolunteerRequestsCount =
        normalizeVolunteeringWeekCapacityCount(project.receivedVolunteerRequestsCount) ?? 0

    return Math.max(0, maxVolunteerCount - receivedVolunteerRequestsCount)
}

export function hasAvailableVolunteeringWeekSpots(
    project: VolunteeringWeekCapacitySource,
    requestedVolunteerCount = 1
): boolean {
    const availableSpots = getAvailableVolunteeringWeekSpots(project)
    const normalizedRequestedCount = normalizeVolunteeringWeekRequestedVolunteerCount(requestedVolunteerCount)

    return availableSpots !== null && normalizedRequestedCount > 0 && normalizedRequestedCount <= availableSpots
}
